'use client';

import { useCallback, useEffect } from 'react';

import { useTranslations } from 'next-intl';

import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import { showErrorToast } from '@/components/ui/ToastNotification';
import { useDeleteComment } from '@/hooks/useDeleteComment';
import { useUpsertComment } from '@/hooks/useUpsertComment';
import { DEVICE_WIDTHS } from '@/types/feedback';

import type { WorkbenchPinPopoverModel } from './types';
import type {
  ProxyCommentReopenedMessage,
  ProxyCommentResolvedMessage,
  ProxyReopenCommentMessage,
  ProxyResolveCommentMessage,
} from '@/types/feedback';
import type { DevicePreset } from '@/types/feedback';

function getBreakpoint(devicePreset: DevicePreset) {
  return {
    id: devicePreset.toUpperCase(),
    minWidth: DEVICE_WIDTHS[devicePreset],
  };
}

interface UseWorkbenchPinPersistenceOptions {
  targetUrl: string;
  feedbackId: string;
  metaSiteId: string;
  device: DevicePreset;
  pinPopoverModel: WorkbenchPinPopoverModel | null;
  setActivePin: (pin: { id: string; x: number; y: number } | null) => void;
}

export function useWorkbenchPinPersistence({
  feedbackId,
  metaSiteId,
  device,
  pinPopoverModel,
  setActivePin,
}: UseWorkbenchPinPersistenceOptions) {
  const t = useTranslations();
  const {
    state: { pins },
    dispatch,
  } = useCommentsContext();
  const { upsertComment } = useUpsertComment();
  const { deleteComment } = useDeleteComment(feedbackId, metaSiteId);

  const handleDismissPinPopover = useCallback(() => {
    const pin = pinPopoverModel?.pin;
    if (pin && !pin.commentId) {
      dispatch({ type: 'DELETE_PIN', payload: { id: pin.id } });
    }
    setActivePin(null);
  }, [pinPopoverModel, dispatch, setActivePin]);

  const handleSavePinPopover = useCallback(() => {
    const pin = pinPopoverModel?.pin;
    if (pin?.label && !pin.commentId) {
      const commentDevice = pin.devicePreset ?? device;
      upsertComment({
        message: pin.label,
        devicePreset: commentDevice,
        location: {
          position: {
            innerPath: pin.pagePath ?? '',
            selector: pin.selector,
            offsetXPct: pin.offsetXPct,
            offsetYPct: pin.offsetYPct,
          },
          fallbackXPct: pin.fallbackXPct,
          fallbackYPct: pin.fallbackYPct,
          breakpoint: getBreakpoint(commentDevice),
        },
      })
        .then(({ commentId, revision }) => {
          dispatch({
            type: 'COMMENT_PERSISTED',
            payload: { pinId: pin.id, commentId, revision },
          });
        })
        .catch(() => showErrorToast(t('get-feedback-toast-saveCommentFailed')));
    } else if (pin?.commentId) {
      const commentDevice = pin.devicePreset ?? device;
      // Legacy pins carry no reconstructed position on the parent (selector '',
      // offsets 0); their reconstruction lives only in the bridge. Sending a
      // location here would overwrite the stored pixel coordinates with zeros
      // and an empty position. Edit message-only and leave the location intact
      // (the update RPC is field-masked, so omitting location preserves it).
      upsertComment({
        commentId: pin.commentId,
        revision: pin.revision,
        message: pin.label,
        ...(pin.isLegacy
          ? {}
          : {
              location: {
                position: {
                  innerPath: pin.pagePath ?? '',
                  selector: pin.selector,
                  offsetXPct: pin.offsetXPct,
                  offsetYPct: pin.offsetYPct,
                },
                fallbackXPct: pin.fallbackXPct,
                fallbackYPct: pin.fallbackYPct,
                breakpoint: getBreakpoint(commentDevice),
              },
            }),
      })
        .then(({ commentId, revision }) =>
          dispatch({
            type: 'COMMENT_PERSISTED',
            payload: { pinId: pin.id, commentId, revision },
          }),
        )
        .catch(() => showErrorToast(t('get-feedback-toast-saveCommentFailed')));
    }
    setActivePin(null);
  }, [pinPopoverModel, device, upsertComment, dispatch, setActivePin, t]);

  const handlePinMovedAndPersist = useCallback(
    (
      id: string,
      selector: string,
      offsetXPct: number,
      offsetYPct: number,
      fallbackXPct: number,
      fallbackYPct: number,
    ) => {
      dispatch({
        type: 'MOVE_PIN',
        payload: {
          id,
          selector,
          offsetXPct,
          offsetYPct,
          fallbackXPct,
          fallbackYPct,
        },
      });
      const pin = pins.find((p) => p.id === id);
      if (pin?.commentId) {
        const commentDevice = pin.devicePreset ?? device;
        // A reposition anchors to the element under the drop point (selector
        // from the bridge). For a legacy pin this writes a real position, so
        // the refetched comment is no longer legacy.
        upsertComment({
          commentId: pin.commentId,
          revision: pin.revision,
          message: pin.label,
          location: {
            position: {
              innerPath: pin.pagePath ?? '',
              selector,
              offsetXPct,
              offsetYPct,
            },
            fallbackXPct,
            fallbackYPct,
            breakpoint: getBreakpoint(commentDevice),
          },
        })
          .then(({ commentId, revision }) =>
            dispatch({
              type: 'COMMENT_PERSISTED',
              payload: { pinId: id, commentId, revision },
            }),
          )
          .catch(() =>
            showErrorToast(t('get-feedback-toast-saveCommentFailed')),
          );
      }
    },
    [device, dispatch, pins, upsertComment, t],
  );

  const handleDeletePinAndPersist = useCallback(
    (id: string) => {
      dispatch({ type: 'DELETE_PIN', payload: { id } });
      const pin = pins.find((p) => p.id === id);
      if (pin?.commentId) {
        deleteComment(pin.commentId).catch(() =>
          showErrorToast(t('get-feedback-toast-deleteCommentFailed')),
        );
      }
    },
    [dispatch, pins, deleteComment, t],
  );

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'FB_COMMENT_RESOLVED') return;
      const { pinId, revision } = (event.data as ProxyCommentResolvedMessage)
        .payload;
      dispatch({
        type: 'UPDATE_PIN_STATUS',
        payload: { id: pinId, status: 'RESOLVED', revision },
      });
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'FB_COMMENT_REOPENED') return;
      const { pinId, revision } = (event.data as ProxyCommentReopenedMessage)
        .payload;
      dispatch({
        type: 'UPDATE_PIN_STATUS',
        payload: { id: pinId, status: 'NEW', revision },
      });
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'FB_REOPEN_COMMENT_FAILED') return;
      showErrorToast(t('get-feedback-toast-reopenCommentFailed'));
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'FB_RESOLVE_COMMENT_FAILED') return;
      showErrorToast(t('get-feedback-toast-resolveCommentFailed'));
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t]);

  const handleResolvePinAndPersist = useCallback(
    (id: string) => {
      const pin = pins.find((p) => p.id === id);
      if (!pin?.commentId) return;
      const message: ProxyResolveCommentMessage = {
        type: 'FB_RESOLVE_COMMENT',
        payload: {
          pinId: id,
          commentId: pin.commentId,
          revision: pin.revision,
          feedbackId,
          metaSiteId,
        },
      };
      window.postMessage(message, window.location.origin);
    },
    [pins, feedbackId, metaSiteId],
  );

  const handleReopenPinAndPersist = useCallback(
    (id: string) => {
      const pin = pins.find((p) => p.id === id);
      if (!pin?.commentId) return;
      const message: ProxyReopenCommentMessage = {
        type: 'FB_REOPEN_COMMENT',
        payload: {
          pinId: id,
          commentId: pin.commentId,
          revision: pin.revision,
        },
      };
      window.postMessage(message, window.location.origin);
    },
    [pins],
  );

  return {
    handleDismissPinPopover,
    handleSavePinPopover,
    handlePinMovedAndPersist,
    handleDeletePinAndPersist,
    handleResolvePinAndPersist,
    handleReopenPinAndPersist,
  };
}
